/**
 * Маршрут регистрации (stage9-signup-invites, design.md Д9): `#/signup` и
 * `#/signup/{token}`. Разбирается до проверки сессии. Токен приглашения не
 * должен оставаться в адресе: его видит PostHog (§103), история и скриншоты.
 * Поэтому он забирается в память и адрес заменяется на `#/signup` — до
 * `initAnalytics` и до первого просмотра экрана.
 */

/** Статус сессии клиента (`SessionState["status"]`), без зависимости от хука. */
type SessionStatus = "checking" | "anonymous" | "authenticated" | "unavailable";

/** Адрес страницы регистрации без токена. */
export const SIGNUP_HASH = "#/signup";

const SIGNUP_PREFIX = "#/signup/";

export interface SignupHash {
  /** Токен ссылки-приглашения; `null` — открытая регистрация. */
  token: string | null;
}

/** Разбор адреса: `null` — это не маршрут регистрации. */
export function parseSignupHash(hash: string): SignupHash | null {
  if (hash === SIGNUP_HASH) {
    return { token: null };
  }
  if (!hash.startsWith(SIGNUP_PREFIX)) {
    return null;
  }
  const raw = hash.slice(SIGNUP_PREFIX.length);
  if (raw === "") {
    return { token: null };
  }
  try {
    return { token: decodeURIComponent(raw) };
  } catch {
    // Токен base64url в кодировании не нуждается; битый `%` оставляем как есть.
    return { token: raw };
  }
}

export function isSignupHash(hash: string): boolean {
  return parseSignupHash(hash) !== null;
}

/**
 * Маршрут подтверждения почты (stage9-email-confirmation, Р11): ссылка из
 * письма `#/confirm/{token}`. Токен забирается в память и адрес заменяется на
 * `#/confirm` до `initAnalytics`, как у приглашения.
 */
export const CONFIRM_HASH = "#/confirm";

const CONFIRM_PREFIX = "#/confirm/";

export interface ConfirmHash {
  /** Токен ссылки подтверждения; `null` — адрес без токена. */
  token: string | null;
}

/** Разбор адреса: `null` — это не маршрут подтверждения. */
export function parseConfirmHash(hash: string): ConfirmHash | null {
  if (hash === CONFIRM_HASH) {
    return { token: null };
  }
  if (!hash.startsWith(CONFIRM_PREFIX)) {
    return null;
  }
  const raw = hash.slice(CONFIRM_PREFIX.length);
  if (raw === "") {
    return { token: null };
  }
  try {
    return { token: decodeURIComponent(raw) };
  } catch {
    return { token: raw };
  }
}

export function isConfirmHash(hash: string): boolean {
  return parseConfirmHash(hash) !== null;
}

/**
 * Маршрут сброса пароля (stage9-password-reset, Р6): ссылка из письма
 * `#/reset/{token}`. Токен забирается в память и адрес заменяется на
 * `#/reset` до `initAnalytics`, как у подтверждения почты.
 */
export const RESET_HASH = "#/reset";

const RESET_PREFIX = "#/reset/";

export interface ResetHash {
  /** Токен ссылки сброса; `null` — адрес без токена. */
  token: string | null;
}

/** Разбор адреса: `null` — это не маршрут сброса пароля. */
export function parseResetHash(hash: string): ResetHash | null {
  if (hash === RESET_HASH) {
    return { token: null };
  }
  if (!hash.startsWith(RESET_PREFIX)) {
    return null;
  }
  const raw = hash.slice(RESET_PREFIX.length);
  if (raw === "") {
    return { token: null };
  }
  try {
    return { token: decodeURIComponent(raw) };
  } catch {
    return { token: raw };
  }
}

export function isResetHash(hash: string): boolean {
  return parseResetHash(hash) !== null;
}

/** Экран запроса сброса пароля: ссылка «Забыли пароль?» с формы входа. */
export const FORGOT_HASH = "#/forgot";

export function isForgotHash(hash: string): boolean {
  return hash === FORGOT_HASH;
}

interface LocationParts {
  hash: string;
  pathname: string;
  search: string;
}

/**
 * Забирает токен из адреса и заменяет адрес на `#/signup` (без записи в
 * историю). Адрес без токена не трогает. Заменитель — параметр: чистую
 * часть проверяют без браузера.
 */
export function takeSignupToken(
  location: LocationParts,
  replace: (url: string) => void,
): string | null {
  const parsed = parseSignupHash(location.hash);
  if (parsed === null || parsed.token === null) {
    return null;
  }
  replace(`${location.pathname}${location.search}${SIGNUP_HASH}`);
  return parsed.token;
}

/** Забирает токен подтверждения из адреса и заменяет адрес на `#/confirm`. */
export function takeConfirmToken(
  location: LocationParts,
  replace: (url: string) => void,
): string | null {
  const parsed = parseConfirmHash(location.hash);
  if (parsed === null || parsed.token === null) {
    return null;
  }
  replace(`${location.pathname}${location.search}${CONFIRM_HASH}`);
  return parsed.token;
}

/** Забирает токен сброса пароля из адреса и заменяет адрес на `#/reset`. */
export function takeResetToken(
  location: LocationParts,
  replace: (url: string) => void,
): string | null {
  const parsed = parseResetHash(location.hash);
  if (parsed === null || parsed.token === null) {
    return null;
  }
  replace(`${location.pathname}${location.search}${RESET_HASH}`);
  return parsed.token;
}

/**
 * Токен, пережидающий смену адреса. Модульное состояние, а не React: забирать
 * приходится в `main.tsx` до рендера и до аналитики.
 */
let heldToken: string | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Забрать токен из адреса и убрать его оттуда. Вызывается до `initAnalytics`
 * и перехватчиком `hashchange` (`watchSignupHash`). Адрес и заменитель —
 * параметры: без них берутся `window.location` и `history.replaceState`.
 */
export function captureSignupToken(
  location: LocationParts = window.location,
  replace: (url: string) => void = (url) =>
    window.history.replaceState(window.history.state, "", url),
): void {
  const token = takeSignupToken(location, replace);
  if (token !== null && token !== heldToken) {
    heldToken = token;
    emit();
  }
}

/**
 * Токен подтверждения почты, отдельно от токена приглашения: маршруты разные,
 * и уход с одного не должен стирать токен другого.
 */
let heldConfirmToken: string | null = null;
const confirmListeners = new Set<() => void>();

function emitConfirm(): void {
  for (const listener of confirmListeners) {
    listener();
  }
}

/** Забрать токен подтверждения из адреса и убрать его оттуда (до `initAnalytics`). */
export function captureConfirmToken(
  location: LocationParts = window.location,
  replace: (url: string) => void = (url) =>
    window.history.replaceState(window.history.state, "", url),
): void {
  const token = takeConfirmToken(location, replace);
  if (token !== null && token !== heldConfirmToken) {
    heldConfirmToken = token;
    emitConfirm();
  }
}

export function getHeldConfirmToken(): string | null {
  return heldConfirmToken;
}

export function subscribeConfirmToken(listener: () => void): () => void {
  confirmListeners.add(listener);
  return () => confirmListeners.delete(listener);
}

/** Токен использован или не нужен — из памяти он уходит вместе со страницей. */
export function clearConfirmToken(): void {
  if (heldConfirmToken !== null) {
    heldConfirmToken = null;
    emitConfirm();
  }
}

/** Токен сброса пароля, отдельно от прочих: маршруты разные. */
let heldResetToken: string | null = null;
const resetListeners = new Set<() => void>();

function emitReset(): void {
  for (const listener of resetListeners) {
    listener();
  }
}

/** Забрать токен сброса из адреса и убрать его оттуда (до `initAnalytics`). */
export function captureResetToken(
  location: LocationParts = window.location,
  replace: (url: string) => void = (url) =>
    window.history.replaceState(window.history.state, "", url),
): void {
  const token = takeResetToken(location, replace);
  if (token !== null && token !== heldResetToken) {
    heldResetToken = token;
    emitReset();
  }
}

export function getHeldResetToken(): string | null {
  return heldResetToken;
}

export function subscribeResetToken(listener: () => void): () => void {
  resetListeners.add(listener);
  return () => resetListeners.delete(listener);
}

/** Токен использован или не нужен — из памяти он уходит вместе со страницей. */
export function clearResetToken(): void {
  if (heldResetToken !== null) {
    heldResetToken = null;
    emitReset();
  }
}

/**
 * Реакция на смену адреса: токены забираются из адреса, а при уходе с маршрута
 * регистрации или подтверждения соответствующий токен забывается — иначе он
 * пережил бы страницу, которой принадлежал.
 */
export function handleSignupHashChange(
  location: LocationParts = window.location,
  replace?: (url: string) => void,
): void {
  if (isSignupHash(location.hash)) {
    captureSignupToken(location, replace);
  } else {
    clearSignupToken();
  }
  if (isConfirmHash(location.hash)) {
    captureConfirmToken(location, replace);
  } else {
    clearConfirmToken();
  }
  if (isResetHash(location.hash)) {
    captureResetToken(location, replace);
  } else {
    clearResetToken();
  }
}

/**
 * Перехват `hashchange` на стадии погружения (`capture`) на `window`: он
 * срабатывает раньше слушателей экрана и аналитики, и те видят адрес уже без
 * токена. Регистрируется один раз в `main.tsx`, до `initAnalytics`.
 */
export function watchSignupHash(target: Pick<Window, "addEventListener"> = window): void {
  target.addEventListener("hashchange", () => handleSignupHashChange(), { capture: true });
}

/**
 * Вошедшему страница регистрации не нужна: адрес маршрута регистрации при
 * живой сессии — это ссылка, вставленная в открытое приложение.
 */
export function signupRouteIsStale(authenticated: boolean, routeActive: boolean): boolean {
  return authenticated && routeActive;
}

/**
 * На `#/forgot` нет `AppShell`, и «Выйти» там нет: переход `authenticated` →
 * `anonymous` на этом экране даёт только погасшая сессия (4401, выход в другой
 * вкладке). Человек ждёт форму входа, а не экран сброса, поэтому хвост
 * `#/forgot` устарел. Вышедший, сам открывший `#/forgot`, такого перехода не даёт.
 */
export function forgotRouteIsStale(
  previous: SessionStatus,
  current: SessionStatus,
  forgotActive: boolean,
): boolean {
  return forgotActive && previous === "authenticated" && current === "anonymous";
}

/**
 * Показан ли экран запроса сброса: адрес `#/forgot` и хвост не устарел. Хвост
 * убирает эффект уже после коммита, а экран и просмотр аналитики определяются
 * в рендере — они не должны успеть увидеть устаревший адрес.
 */
export function forgotRouteShown(
  previous: SessionStatus,
  current: SessionStatus,
  forgotActive: boolean,
): boolean {
  return forgotActive && !forgotRouteIsStale(previous, current, forgotActive);
}

/**
 * Нужно ли перечитать сессию после смены пароля (`204`). Анонимному незачем:
 * сессии учётной записи отозваны, а входа не было. Остальным — да, включая
 * `unavailable`: иначе человек увидел бы «Демон недоступен».
 */
export function sessionNeedsRecheck(status: SessionStatus): boolean {
  return status !== "anonymous";
}

/**
 * Токен ссылки подтверждения, который надо отправить демону. От сессии не
 * зависит: ссылка из письма может прийти в браузер, где другая учётная запись
 * уже вошла, и её нельзя ни молча выбросить, ни оставить непотраченной. `null`
 * — отправлять нечего (маршрут закрыт или токена в памяти нет).
 */
export function confirmTokenToSubmit(routeActive: boolean, token: string | null): string | null {
  return routeActive ? token : null;
}

/**
 * Сообщение формы входа при данном статусе сессии. Перечитанная действующая
 * сессия гасит «Почта подтверждена, войдите» и «Пароль изменён, войдите»
 * (сбросили пароль другой записи) — иначе они всплыли бы на форме входа после
 * следующего выхода.
 */
export function loginNoticeForSession(status: SessionStatus, notice: string | null): string | null {
  return status === "authenticated" ? null : notice;
}

export function getHeldSignupToken(): string | null {
  return heldToken;
}

export function subscribeSignupToken(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Токен использован или не нужен — из памяти он уходит вместе с формой. */
export function clearSignupToken(): void {
  if (heldToken !== null) {
    heldToken = null;
    emit();
  }
}

/**
 * Уйти со страницы регистрации (или подтверждения) на корень без записи в историю: «назад» не
 * должен возвращать на форму уже вошедшего. `replaceState` `hashchange` не
 * шлёт — подписчикам адреса сообщаем сами.
 */
export function leaveSignupRoute(): void {
  window.history.replaceState(window.history.state, "", "#/");
  window.dispatchEvent(new HashChangeEvent("hashchange"));
}
